/* Create / edit campaign — one column: name, active, message source (Mass) or
   keep-in-DMs (Priority), list weights, audience and timing, with the gap
   between messages folded under Advanced. Save stays blocked until the problem
   shown in the footer is fixed. */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { AdvancedToggle, Icon, Seg, Spin, Toggle } from '../../ChatAssistant/compact/kit';
import { font as f } from '../../ChatAssistant/compact/style';
import { C } from '../../ChatAssistant/compact/tokens';
import type { MsgList } from './massApi';
import { DELETED_COLOR, campIssues, distTotal, listColor, payloadOf, splitEvenly, stepRange, type Camp, type Issue, type Kind, type Platform } from './model';
import { BlockHead, Dialog, DialogHead, IconBtn, Menu, Panel, PanelRow, Stepper } from './parts';
import { FANSLY_SYSTEM_AUDIENCE_OPTIONS } from '../fanslyAudience';
import { AddLists } from '../../ChatAssistant/compact/setup/parts';

interface AudienceOpt { id: string; label: string }

export function CampaignModal({ kind, accountId, platform, initial, lists, onClose, onSave }: {
  kind: Kind; accountId: string; platform: Platform; initial: Camp; lists: MsgList[];
  onClose: () => void; onSave: (c: Camp, body: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useTranslation('massCompact');
  const [c, setC] = useState<Camp>(() => (platform === 'fansly' && kind === 'mass' && initial.inc.length === 0
    ? { ...initial, inc: ['__fansly_group_flags_32__'] }
    : initial));
  const [adv, setAdv] = useState(false);
  const [keepAsk, setKeepAsk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aud, setAud] = useState<AudienceOpt[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const isNew = initial.id === null;
  const fansly = platform === 'fansly';
  const systemAudience = kind === 'priority' ? FANSLY_SYSTEM_AUDIENCE_OPTIONS.slice(0, 3) : FANSLY_SYSTEM_AUDIENCE_OPTIONS;

  const loadAudience = async (resync = false) => {
    if (fansly) {
      const ls = await api.getFanslyAudienceLists(accountId);
      setAud([...systemAudience, ...ls.map((l) => ({ id: String(l.id), label: l.name }))]);
    } else {
      if (resync) await api.refreshListsAndWait(accountId);
      const ls = await api.getAccountOfLists(accountId);
      setAud(ls.filter((l) => l.of_list_name).map((l) => ({ id: l.of_list_name, label: l.of_list_name })));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadAudience().catch(() => setAud(fansly ? [...systemAudience] : []));
  }, [accountId, fansly]);
  const refresh = async () => {
    setRefreshing(true);
    try { await loadAudience(true); } catch { console.error('Failed to refresh lists'); } finally { setRefreshing(false); }
  };

  const set = (p: Partial<Camp>) => setC((x) => ({ ...x, ...p }));
  const idx = useMemo(() => new Map(lists.map((l, i) => [String(l.id), i])), [lists]);
  const listIds = useMemo(() => new Set(idx.keys()), [idx]);
  const nameOf = (id: string) => { const i = idx.get(id); return i === undefined ? t('camp.deletedList') : lists[i]!.name; };
  const colorOf = (id: string) => { const i = idx.get(id); return i === undefined ? DELETED_COLOR : listColor(i); };
  const audLabel = (id: string) => aud?.find((o) => o.id === id)?.label
    ?? FANSLY_SYSTEM_AUDIENCE_OPTIONS.find((o) => o.id === id)?.label
    ?? (id.startsWith('__fansly_group_flags_') ? 'Fansly audience' : id);

  const usesLists = !(kind === 'mass' && c.source === 'auto');
  const tot = distTotal(c.dist);
  const free = lists.filter((l) => !c.dist.some((d) => d.id === String(l.id)));
  const issues = campIssues(c, kind, listIds, platform);
  const issueText = (i: Issue) => (i.code === 'weights' ? t('modal.issue.weights', { total: i.total }) : t(`modal.issue.${i.code}`));

  const save = async () => {
    if (issues.length || saving) return;
    setSaving(true);
    try { await onSave(c, payloadOf(c, kind, platform)); }
    catch (e) { console.error('Failed to save campaign:', e); alert(isNew ? t('modal.createFailed') : t('modal.saveFailed')); setSaving(false); }
  };

  const range = (key: 'vis' | 'gap', step: number, lo: number, hi: number) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: f(400, 12), color: C.sub }}>
      <span>{t('modal.from')}</span>
      <Stepper value={c[key][0]} onDec={() => set({ [key]: stepRange(c[key], 0, -step, lo, hi) })} onInc={() => set({ [key]: stepRange(c[key], 0, step, lo, hi) })} />
      <span>{t('modal.to')}</span>
      <Stepper value={c[key][1]} onDec={() => set({ [key]: stepRange(c[key], 1, -step, lo, hi) })} onInc={() => set({ [key]: stepRange(c[key], 1, step, lo, hi) })} />
      <span>{t('modal.min')}</span>
    </div>
  );

  return (
    <>
      <Dialog width={720} onClose={onClose} locked={keepAsk || saving}>
        <DialogHead icon="target" title={isNew ? t('modal.newTitle') : t('modal.editTitle')} sub={t('modal.sub')} onClose={onClose} closeTitle={t('close')} />
        <div className="cpt-thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 18px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Panel>
            <PanelRow label={t('modal.name')} minHeight={46}>
              <input value={c.name} autoFocus={isNew} onChange={(e) => set({ name: e.target.value })} placeholder={t('modal.namePh')} style={{ width: 300, maxWidth: '100%', height: 28, padding: '0 9px', boxSizing: 'border-box', background: C.card, border: `1px solid ${c.name.trim() ? C.border : 'color-mix(in srgb, var(--om-accent) 60%, transparent)'}`, borderRadius: 7, color: C.text, font: f(500, 12), outline: 'none' }} />
            </PanelRow>
            <PanelRow label={t('modal.active')} help={c.active ? t('modal.activeOn') : t('modal.activeOff')} minHeight={46}>
              <Toggle on={c.active} onChange={(v) => set({ active: v })} />
            </PanelRow>
            {kind === 'mass' && (
              <PanelRow label={t('modal.source')} help={usesLists ? t('modal.sourceLists') : t('modal.sourceAuto')} minHeight={46}>
                <Seg value={c.source} onChange={(v) => set({ source: v })} options={[{ id: 'auto', label: t('modal.auto') }, { id: 'custom', label: t('modal.myLists') }]} />
              </PanelRow>
            )}
            {kind === 'priority' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px 10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, minHeight: 30, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ font: f(500, 12.5), color: C.text }}>{t('modal.keep')}</span>
                    <span style={{ font: f(400, 11.5), color: C.sub }}>{t('modal.keepHelp')}</span>
                  </div>
                  <Toggle on={c.keep} onChange={(v) => (v ? setKeepAsk(true) : set({ keep: false }))} />
                </div>
                {c.keep && (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: 'color-mix(in srgb, var(--om-accent) 12%, transparent)', border: `1px solid ${C.tintLine}`, borderRadius: 7, font: f(500, 12, 1.45), color: C.purplePale }}>
                    <Icon name="alert-circle" size={13} style={{ flex: 'none', marginTop: 2 }} /><span>{t('modal.keepOn')}</span>
                  </div>
                )}
              </div>
            )}
          </Panel>

          {usesLists && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <BlockHead title={t('modal.dist')} hint={t('modal.distHint')} />
              <Panel>
                {c.dist.map((d, j) => {
                  const gone = !idx.has(d.id);
                  return (
                    <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 12, height: 42, padding: '0 6px 0 12px' }}>
                      <span style={{ width: 10, height: 10, flex: 'none', borderRadius: 3, background: colorOf(d.id) }} />
                      <span style={{ flex: 1, minWidth: 0, font: f(500, 12.5), color: gone ? C.sub : C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(d.id)}</span>
                      <div style={{ width: 120, height: 4, flex: 'none', borderRadius: 99, background: C.border, overflow: 'hidden' }}><div style={{ height: '100%', width: `${Math.min(100, d.w)}%`, background: colorOf(d.id) }} /></div>
                      <Stepper minWidth={38} value={`${d.w}%`} onDec={() => set({ dist: c.dist.map((x, k) => (k === j ? { ...x, w: Math.max(0, x.w - 5) } : x)) })} onInc={() => set({ dist: c.dist.map((x, k) => (k === j ? { ...x, w: Math.min(100, x.w + 5) } : x)) })} />
                      <IconBtn icon="x" title={t('modal.removeList')} size={26} color={C.muted} onClick={() => set({ dist: c.dist.filter((_, k) => k !== j) })} />
                    </div>
                  );
                })}
                {!c.dist.length && <div style={{ padding: 14, font: f(400, 12), color: C.muted, textAlign: 'center' }}>{t('modal.noDist')}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 42, padding: '6px 12px', flexWrap: 'wrap', boxSizing: 'border-box' }}>
                  <Menu
                    look="dashed"
                    label={free.length ? t('modal.addList', { count: free.length }) : t('modal.allLists')}
                    disabled={!free.length}
                    options={free.map((l) => ({ id: String(l.id), label: l.name, sub: t('lists.msgsShort', { count: l.message_count ?? 0 }) }))}
                    onPick={(id) => set({ dist: [...c.dist, { id, w: Math.max(0, 100 - tot) }] })}
                  />
                  <button type="button" onClick={() => set({ dist: splitEvenly(c.dist) })} disabled={!c.dist.length} className="cpt-hover-icon" style={{ height: 28, padding: '0 9px', border: 0, borderRadius: 7, background: 'transparent', color: C.sub, font: f(500, 12), cursor: c.dist.length ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                    <Icon name="sliders" size={12} />{t('modal.split')}
                  </button>
                  <span style={{ flex: 1 }} />
                  <div style={{ display: 'flex', gap: 1, width: 120, height: 4, borderRadius: 99, overflow: 'hidden', background: C.border }}>
                    {c.dist.map((d) => <span key={d.id} style={{ width: `${Math.min(d.w, 100)}%`, background: colorOf(d.id) }} />)}
                  </div>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, font: f(600, 12), color: tot === 100 ? C.text : C.purplePale, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    <Icon name={tot === 100 ? 'check' : 'alert-circle'} size={12} />{tot === 100 ? t('modal.balanced') : t('modal.ofHundred', { total: tot })}
                  </span>
                </div>
              </Panel>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <BlockHead
              title={t('modal.audience')}
              hint={fansly && kind === 'priority'
                ? 'Priority sends to existing Fansly chats. Creator and offline exclusions require a native mass message.'
                : fansly ? t('modal.audienceHintFansly') : t('modal.audienceHint')}
              right={(
                <button type="button" onClick={() => { void refresh(); }} disabled={refreshing} className="cpt-hover-icon" title={t('modal.refreshTip')} style={{ height: 24, padding: '0 8px', border: 0, borderRadius: 6, background: 'transparent', color: C.sub, font: f(500, 11.5), cursor: refreshing ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                  {refreshing ? <Spin size={11} /> : <Icon name="refresh-cw" size={11} />}{t('modal.refresh')}
                </button>
              )}
            />
            <Panel>
              {(['inc', 'exc'] as const).map((key) => {
                const inc = key === 'inc';
                const pool = (aud ?? [])
                  .filter((o) => inc || !o.id.startsWith('__fansly_group_flags_'))
                  .filter((o) => !c.inc.includes(o.id) && !c.exc.includes(o.id));
                return (
                  <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '10px 12px', flexWrap: 'wrap' }}>
                    <span style={{ flex: '0 0 120px', paddingTop: 4, font: f(500, 12.5), color: C.text }}>{inc ? t('modal.sendTo') : t('modal.skip')}</span>
                    <div style={{ flex: '1 1 300px', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      {c[key].map((id) => (
                        <span key={id} style={{ height: 26, padding: '0 4px 0 9px', display: 'flex', alignItems: 'center', gap: 5, boxSizing: 'border-box', background: inc ? C.tint16 : C.card, border: `1px solid ${inc ? C.tintLine : C.borderHi}`, borderRadius: 7, font: f(500, 12), color: inc ? 'var(--om-accent-4)' : 'var(--om-body)', whiteSpace: 'nowrap' }}>
                          {audLabel(id)}
                          <button type="button" title={t('modal.removeAud')} onClick={() => set({ [key]: c[key].filter((x) => x !== id) })} style={{ width: 18, height: 18, border: 0, borderRadius: 4, background: 'transparent', color: 'inherit', opacity: 0.7, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="x" size={11} /></button>
                        </span>
                      ))}
                      <AddLists
                        height={26}
                        label={aud === null ? '…' : t('modal.addAud', { count: pool.length })}
                        disabled={aud === null || !pool.length}
                        pool={pool}
                        onAdd={(ids) => set({ [key]: [...c[key], ...ids] })}
                      />
                    </div>
                  </div>
                );
              })}
            </Panel>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <BlockHead title={t('modal.timing')} hint={t('modal.timingHint')} />
            <Panel>
              {c.keep ? (
                <PanelRow label={t('modal.vis')} help={t('modal.visKept')} minHeight={50}>
                  <span style={{ font: f(500, 12), color: C.purplePale, whiteSpace: 'nowrap' }}>{t('modal.neverDeleted')}</span>
                </PanelRow>
              ) : (
                <PanelRow label={t('modal.vis')} help={t('modal.visHelp')} minHeight={50}>{range('vis', 5, 5, 600)}</PanelRow>
              )}
            </Panel>
            <AdvancedToggle open={adv} onToggle={() => setAdv((x) => !x)} label={t('modal.advanced')} hint={t('modal.advHint', { a: c.gap[0], b: c.gap[1] })} />
            {adv && (
              <Panel>
                <PanelRow label={t('modal.gap')} help={t('modal.gapHelp')} minHeight={50}>{range('gap', 1, 0, 240)}</PanelRow>
              </Panel>
            )}
          </div>
        </div>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderTop: `1px solid ${C.divider}` }}>
          <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, font: f(500, 12), color: C.purplePale, minWidth: 0 }}>
            {issues.length > 0 && <Icon name="alert-circle" size={13} style={{ flex: 'none' }} />}
            {issues.length ? issueText(issues[0]!) : isNew ? t('modal.readyCreate') : t('modal.readySave')}
          </span>
          <button type="button" onClick={onClose} className="cpt-hover-icon" style={{ height: 30, padding: '0 12px', border: `1px solid ${C.borderHi}`, borderRadius: 7, background: C.input, color: C.text, font: f(500, 12.5), cursor: 'pointer' }}>{t('cancel')}</button>
          <button type="button" onClick={() => { void save(); }} disabled={!!issues.length || saving} style={{ height: 30, padding: '0 14px', border: 0, borderRadius: 7, background: issues.length ? C.borderLo : C.purple, color: issues.length ? C.muted : '#fff', font: f(600, 12.5), cursor: issues.length || saving ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            {saving && <Spin size={12} />}{isNew ? t('modal.create') : t('modal.save')}
          </button>
        </div>
      </Dialog>

      {keepAsk && (
        <Dialog width={400} z={1010} onClose={() => setKeepAsk(false)} style={{ padding: 18, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 30, height: 30, flex: 'none', borderRadius: 8, background: C.tint16, color: C.purplePale, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="alert-circle" size={15} /></span>
            <span style={{ font: f(700, 14), color: C.text }}>{t('keepAsk.title')}</span>
          </div>
          <span style={{ font: f(400, 12.5, 1.5), color: C.sub }}>{t('keepAsk.body')}</span>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" autoFocus onClick={() => setKeepAsk(false)} style={{ height: 30, padding: '0 12px', border: 0, borderRadius: 7, background: C.purple, color: '#fff', font: f(600, 12.5), cursor: 'pointer' }}>{t('keepAsk.no')}</button>
            <button type="button" onClick={() => { set({ keep: true }); setKeepAsk(false); }} className="cpt-hover-icon" style={{ height: 30, padding: '0 12px', border: `1px solid ${C.borderHi}`, borderRadius: 7, background: C.input, color: C.text, font: f(500, 12.5), cursor: 'pointer' }}>{t('keepAsk.yes')}</button>
          </div>
        </Dialog>
      )}
    </>
  );
}
